import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';

const CompanySettingsPermissions = ({ 
  permissions, 
  roleForm, 
  setRoleForm 
}) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState({
    company_information: false,
    business_address: false,
    contact_information: false,
    primary_contact: false,
    business_settings: false
  });
  
  // Company Settings pages mapping
  const companySettingsPages = [
    'company_information',
    'business_address', 
    'contact_information',
    'primary_contact',
    'business_settings'
  ];
  
  const pageNameMapping = {
    'company_information': 'Company Information Page',
    'business_address': 'Business Address Page',
    'contact_information': 'Contact Information Page',
    'primary_contact': 'Primary Contact Page',
    'business_settings': 'Business Settings Page'
  };
  
  const pageIconMapping = {
    'company_information': <BusinessIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'business_address': <LocationOnIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'contact_information': <ContactPhoneIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'primary_contact': <PersonIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'business_settings': <SettingsIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
  };

  const renderCompanySettingsPagePermissions = (pageKey, pageName) => {
    const isExpanded = expandedSections[pageKey] !== false;
    let pagePermissions = [];
    
    if (pageKey === 'company_information') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('settings.company_information'));
    } else if (pageKey === 'business_address') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('settings.business_address'));
    } else if (pageKey === 'contact_information') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('settings.contact_information'));
    } else if (pageKey === 'primary_contact') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('settings.primary_contact'));
    } else if (pageKey === 'business_settings') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('settings.business_settings'));
    }

    if (pagePermissions.length === 0) return null;

    const viewPerm = pagePermissions.find(p => p.name?.includes('.view'));
    const editPerm = pagePermissions.find(p => p.name?.includes('.edit'));

    return (
      <Box 
        key={pageKey}
        sx={{ 
          mb: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'white'
        }}
      >
        {/* Page Header */}
        <Box
          onClick={() => setExpandedSections(prev => ({ ...prev, [pageKey]: !isExpanded }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {pageIconMapping[pageKey]}
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {pageName}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
        </Box>

        {/* Permission Switches */}
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            {/* View Permission */}
            {viewPerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: editPerm ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    View
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can view {pageName.toLowerCase()}
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[viewPerm.id]?.read || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[viewPerm.id]) {
                      newPermissions[viewPerm.id] = {};
                    }
                    newPermissions[viewPerm.id].read = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Edit Permission */}
            {editPerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Edit
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can edit {pageName.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[editPerm.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[editPerm.id]) {
                      newPermissions[editPerm.id] = {};
                    }
                    newPermissions[editPerm.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      {companySettingsPages.map(pageKey => {
        const pageName = pageNameMapping[pageKey] || pageKey;
        return renderCompanySettingsPagePermissions(pageKey, pageName);
      })}
    </Box>
  );
};

export default CompanySettingsPermissions;
